import { styled } from '@mui/material/styles';
import { Box, Container, Typography, Button, Grid, useTheme } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { fadeUp, staggerContainer, reducedMotionVariants } from '../../../theme/animations';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StoreIcon from '@mui/icons-material/Store';
import { useLandingAnalytics } from '../hooks/useLandingAnalytics';
import {
  GameController,
  PoolBall,
  Balloon,
} from '../../../components/ui/illustrations/venue-themed';

const CTAContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  position: 'relative',
  background: 'linear-gradient(135deg, #0F0F0F 0%, #1F1F3D 50%, #2D1F4D 100%)',
  backgroundSize: '200% 200%',
  animation: 'gradient-shift 10s ease infinite',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(circle at 30% 50%, rgba(168, 85, 247, 0.15) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
}));

const CTABox = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: 24,
  padding: theme.spacing(6),
  textAlign: 'center',
  transition: 'all 0.3s',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderColor: 'rgba(255, 255, 255, 0.2)',
    transform: 'translateY(-4px)',
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4),
  },
}));

const CTATitle = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontWeight: 700,
  fontSize: '1.75rem',
  color: theme.palette.common.white,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    fontSize: '1.5rem',
  },
}));

const CTADescription = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '1rem',
  color: 'rgba(255, 255, 255, 0.7)',
  marginBottom: theme.spacing(4),
  lineHeight: 1.6,
}));

const CTAButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})<{ bgColor?: string }>(({ bgColor, theme }) => ({
  fontFamily: theme.typography.fontFamilyMono,
  fontWeight: 500,
  textTransform: 'none',
  fontSize: '1rem',
  padding: '14px 32px',
  borderRadius: 12,
  backgroundColor: bgColor || theme.palette.common.white,
  color: bgColor ? theme.palette.common.white : theme.palette.common.black,
  border: bgColor ? 'none' : '2px solid rgba(255, 255, 255, 0.2)',
  '&:hover': {
    backgroundColor: bgColor || 'rgba(255, 255, 255, 0.9)',
    transform: 'scale(1.05)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
}));

export function CTASection() {
  const theme = useTheme();
  const { track } = useLandingAnalytics();
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = shouldReduceMotion ? reducedMotionVariants : staggerContainer;
  const itemVariants = shouldReduceMotion ? reducedMotionVariants : fadeUp;

  return (
    <CTAContainer>
      <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <motion.div
          style={{ position: 'absolute', top: '15%', left: '8%', opacity: 0.15 }}
          animate={shouldReduceMotion ? {} : { y: [0, -10, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <GameController size={50} color="rgba(168, 85, 247, 0.6)" />
        </motion.div>
        <motion.div
          style={{ position: 'absolute', bottom: '20%', right: '10%', opacity: 0.15 }}
          animate={shouldReduceMotion ? {} : { y: [0, -8, 0] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        >
          <PoolBall size={40} color="rgba(16, 185, 129, 0.6)" />
        </motion.div>
        <motion.div
          style={{ position: 'absolute', top: '30%', right: '25%', opacity: 0.12 }}
          animate={shouldReduceMotion ? {} : { y: [0, -12, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        >
          <Balloon size={45} color="rgba(251, 146, 60, 0.6)" />
        </motion.div>
      </Box>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <Grid container spacing={4}>
            {/* Vendor CTA */}
            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div variants={itemVariants}>
                <CTABox>
                  <IconWrapper>
                    <StoreIcon sx={{ fontSize: 32, color: theme.palette.common.white }} />
                  </IconWrapper>
                  <CTATitle>Own a Venue?</CTATitle>
                  <CTADescription>
                    List your venue for free. Manage bookings, track revenue, and handle check-ins —
                    all from one dashboard. Get bookings from day one.
                  </CTADescription>
                  <CTAButton
                    href="/list-your-venue"
                    bgColor="#6B8AFB"
                    endIcon={<ArrowForwardIcon />}
                    fullWidth
                    onClick={() => track({ action: 'cta_click', variant: 'vendor' })}
                  >
                    List Your Venue
                  </CTAButton>
                </CTABox>
              </motion.div>
            </Grid>

            {/* Customer CTA */}
            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div variants={itemVariants}>
                <CTABox>
                  <IconWrapper>
                    <ArrowForwardIcon sx={{ fontSize: 32, color: theme.palette.common.white }} />
                  </IconWrapper>
                  <CTATitle>Ready to Book?</CTATitle>
                  <CTADescription>
                    Every venue on our platform is personally verified. Transparent pricing, instant
                    confirmation, and a seamless booking experience.
                  </CTADescription>
                  <CTAButton
                    href="/venues"
                    endIcon={<ArrowForwardIcon />}
                    fullWidth
                    onClick={() => track({ action: 'cta_click', variant: 'customer' })}
                  >
                    Browse Venues Near You
                  </CTAButton>
                </CTABox>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </CTAContainer>
  );
}
